import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";

import useEquipmentService from "../../../services/equipmentService";

import BasicDialog from "../../base/BasicDialog";
import AutocompleteField from "../../field/AutocompleteField";
import TextFieldFormControl from "../../field/TextFieldFormControl";
import FloatField from "../../base/FloatField";
import DateTimeField from "../../field/DateTimeField";

import LoaderWrapper from "../../wrapper/LoaderWrapper";

import { useForm } from "../../../hooks/useForm";
import {
  getErrorMsg,
  convertFlatListFromBackendToTranslatedOptionObject,
} from "../../../helpers/methods";

import { useSnackbarAlert } from "../../../context/snackbarAlert";

import useFileService from "../../../services/fileService";
import useFileOnMemoryData from "../../../hooks/useFileOnMemoryData";

import {
  EQUIPBMS_STATUS_MANUAL_ACCEPTED,
  EQUIPBMS_STATUS_TO_VERIFICATION,
} from "../../button/ChangeEquipmentBmsStatusButton/ChangeEquipmentBmsStatusButton";

const EquipmentBmsCreateDialog = (props) => {
  const { t } = useTranslation();

  const {
    formValue,
    setFormValue,
    onChange,
    onChangeDateTime,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm({
    equipbms_date: new Date(),
  });

  const snackbarAlert = useSnackbarAlert();

  const { createEquipmentBms, getEquipmentSelectList } = useEquipmentService();

  const equipmentList = useAsync(getEquipmentSelectList);

  const [
    enclosures,
    onAddEnclosure,
    onUpdateEnclosure,
    onDeleteEnclosure,
    prepareEnclosuresToSend,
  ] = useFileOnMemoryData();

  const { downloadFileByBlobUrl } = useFileService();

  const onDownloadEnclosure = (enclosureId, enclosureName, index) => {
    const enclosure = enclosures[index];
    downloadFileByBlobUrl(enclosure.blob_url, enclosureName);
  };

  const getPreparedDataToSend = () => {
    let tempFormValue = { ...formValue };
    tempFormValue["files"] = prepareEnclosuresToSend(enclosures);
    return tempFormValue;
  };

  const createEquipmentBmsFn = useAsyncFn(createEquipmentBms);
  const handleSubmit = (dataToSend) => {
    createEquipmentBmsFn
      .execute(dataToSend)
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.reading_added")
        );
        if (props.onSubmitCallback) {
          props.onSubmitCallback(res);
        }
        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          getErrorMsg(
            error.data,
            t("snackbar_alert.occurred_error_during_saving_changes")
          )
        );
      });
  };

  const isFormValid =
    formValue.equipment &&
    formValue.equipbms_value &&
    formValue.equipbms_date &&
    formValue.equipbms_status;

  const getDialogContent = () => {
    if (equipmentList.loading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
      >
        <Grid item xs={12}>
          <AutocompleteField
            fullWidth
            name={"equipment"}
            options={equipmentList.value}
            isObjectOption={true}
            optionLabelKey={"equipment_nr"}
            variant={"outlined"}
            label={`${t("form.meter_passport.equipment_nr")}`}
            value={formValue.equipment}
            required={true}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={false}
            addNewValue={false}
            disableClearable={true}
          />
        </Grid>
        <Grid item xs={12}>
          <AutocompleteField
            name="equipbms_status"
            label={t("table.counter_repository.equipbms_status")}
            value={formValue?.equipbms_status}
            options={convertFlatListFromBackendToTranslatedOptionObject([
              EQUIPBMS_STATUS_MANUAL_ACCEPTED,
              EQUIPBMS_STATUS_TO_VERIFICATION,
            ])}
            isObjectOption={true}
            addNewValue={false}
            onChange={onChangeAutocompleteFieldWithObjectOptions}
            multiple={false}
            disableClearable
          />
        </Grid>
        <Grid item xs={6}>
          <FloatField
            name={"equipbms_value"}
            label={t("form.meter_passport.new_read")}
            value={formValue.equipbms_value}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={6}>
          <DateTimeField
            name={"equipbms_date"}
            label={t("form.meter_passport.read_date")}
            value={formValue.equipbms_date}
            onChange={onChangeDateTime}
            readOnly={false}
            showClearIcon={false}
          />
        </Grid>
        <Grid item xs={12}>
          <TextFieldFormControl
            name={"equipbms_note"}
            label={t("form.meter_passport.new_read_note")}
            value={formValue.equipbms_note}
            onChange={onChange}
            multiline
            rows={2}
          />
        </Grid>
        {/* TODO uncoment when add enclosure is neaded */}
        {/* <Grid item xs={12}>
          <FileUploadList
            addEnclosureButtonProps={{ size: "mini" }}
            defaultEnclosureType={INTERNAL_TYPE}
            enclosures={enclosures}
            onDownloadEnclosure={onDownloadEnclosure}
            onAddEnclosure={onAddEnclosure}
            onDeleteEnclosure={onDeleteEnclosure}
            onUpdateEnclosure={onUpdateEnclosure}
            showExtendedAddEnclosureDialog={true}
            showSubcontractorSelect={false}
            showEnclosureType={false}
            showEnclosurePartKind={false}
            allowCopyEnclosure={false}
            multiple={false}
          />
        </Grid> */}
        <Grid item xs={12}>
          <LoadingButton
            fullWidth
            variant="contained"
            color="primary"
            loading={createEquipmentBmsFn.loading}
            onClick={() => handleSubmit(getPreparedDataToSend())}
            disabled={!isFormValid}
          >
            {t("add")}
          </LoadingButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      title={t("form.meter_passport.add_equipemnt_bms")}
      maxWidth={"sm"}
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentBmsCreateDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitCallback: PropTypes.func,
};

EquipmentBmsCreateDialog.defaultProps = {};

export default EquipmentBmsCreateDialog;
